import { ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/solid';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';
import { deleteRoverTdm, patchRoverTdm } from '@/api/Rover/applicationTDM';
import type { IPatchTdmPayload } from '@/api/Rover/applicationTDM/types';
import ImageCircularAvatar from '@/components/UI/Avatar/ImageCircularAvatar';
import Badge from '@/components/UI/Badge';
import InputField from '@/components/UI/Form/InputField';
import ReactSelect from '@/components/UI/Form/ReactSelect';
import Confirm from '@/components/UI/Modal/Confirm';
import { tdmStatus } from '@/constants/Project';
import { RoverReactQueryKey, TdmReactQueryKey } from '@/constants/ReactQueryKeys';
import { dueDiligenceList } from '@/constants/Rover/Applications';
import { useApplicationTeamMembers } from '@/hooks/ReactQueryHooks/useApplicationQuery';
import { useSherpaVendors } from '@/hooks/ReactQueryHooks/useVendorQuery';
import { notifyUser } from '@/store/Toast';
import type { ReactSelectOption } from '@/types/Constants';
import type { TdmType } from '@/types/Tdm';
import type { GetVendorsResponse } from '@/types/Vendor.ts';
import { formatDate3, getInitials, sanitizeResponseError } from '@/utils';
import ChangeStatusForm from './ChangeStatusForm';

const tdmQueryKeyMapping = {
  task: TdmReactQueryKey.GET_APPLICATION_TASKS,
  deliverable: TdmReactQueryKey.GET_APPLICATION_DELIVERABLES,
  milestone: TdmReactQueryKey.GET_APPLICATION_MILESTONES,
};

const roverQueryKeyMapping = {
  task: RoverReactQueryKey.GET_ROVER_TASKS,
  deliverable: RoverReactQueryKey.GET_ROVER_DELIVERABLES,
  milestone: RoverReactQueryKey.GET_ROVER_MILESTONES,
};

const validationSchema = Yup.object({
  name: Yup.string().max(250, 'Must be less than 250 characters').required('required'),
  status: Yup.string().nullable(),
  notes: Yup.string().nullable().max(1000, 'Must be less than 1000 characters'),
  leadId: Yup.number().required('required'),
});

export interface TdmDescriptionProps {
  tdm: any;
  tdmType: TdmType;
}
export default function TdmDescription({ tdm, tdmType }: TdmDescriptionProps) {
  const params = useParams<string>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isEditing, setIsEditing] = useState<{ [key: string]: boolean }>({
    name: false,
    leadId: false,
    supportId: false,
    vendorId: false,
    notes: false,
    tdmStatus: false,
    startDate: false,
    dueDate: false,
  });

  const removeTdm = useMutation(() => deleteRoverTdm({ tdmType, tdmId: tdm?.id }), {
    onSuccess: () => {
      queryClient.invalidateQueries([roverQueryKeyMapping[tdmType]]);
      queryClient.invalidateQueries([tdmQueryKeyMapping[tdmType]]);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: `${tdmType} delete successful`,
          toastType: 'success',
        })
      );
      setShowDeleteConfirm(false);
      //-1 navigates back to the page where it came from
      navigate(-1);
    },
    onError: (error: any) => {
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: `${tdmType} delete failed. ${resError}`,
          toastType: 'failed',
        })
      );
    },
  });
  const phase = dueDiligenceList.find((diligence) => diligence?.value === tdm?.phaseId);
  const getApplicationTeamMembers = useApplicationTeamMembers({
    applicationId: params?.applicationId,
  });
  const applicationTeamMembers = getApplicationTeamMembers?.data?.data;
  const teams = applicationTeamMembers?.members?.map((member: any) => {
    return {
      label: member?.user?.name,
      value: member?.user?.id.toString(),
    };
  });

  let leads = [];
  let supports = [];
  if (teams) {
    leads = teams.filter((lead: any) => lead?.label !== tdm.lead.name && lead?.label !== tdm?.support?.name);

    supports = teams.filter((support: any) => {
      return support?.label !== tdm?.support?.name && support?.label !== tdm?.lead?.name;
    });
  }

  const queryClient = useQueryClient();
  const updateTdm = useMutation(
    (data: Partial<IPatchTdmPayload>) =>
      patchRoverTdm({
        tdmType: tdmType,
        tdmId: tdm?.id,
        data,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([roverQueryKeyMapping[tdmType]]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${tdmType} updated successfully`,
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${tdmType} update failed ${resError}.`,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const statusModalHandler = () => {
    if (tdm?.status === 'UNDER_REVIEW') {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: `Approval action is pending on this ${tdmType}.`,
          toastType: 'failed',
        })
      );
    } else {
      setIsEditing((prevState) => {
        return { ...prevState, tdmStatus: !prevState.tdmStatus };
      });
    }
  };

  const deleteTdmHandler = () => {
    setShowDeleteConfirm(true);
  };

  const cancelDeleteTdm = () => {
    setShowDeleteConfirm(false);
  };

  const tdmDefaultValues = {
    ...(tdm.name && { name: tdm.name }),
    ...(tdm.name && { name: tdm.name }),
    startDate: formatDate3(tdm?.startDate, 'yyyy-mm-dd'),
    dueDate: formatDate3(tdm?.dueDate, 'yyyy-mm-dd'),
    notes: tdm?.notes,
  };

  const {
    register,
    resetField,
    setValue,
    getValues,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: tdmDefaultValues,
    resolver: yupResolver(validationSchema),
    mode: 'all',
  });

  const updateTdmHandler = async (fieldName: string) => {
    let data = { [fieldName]: getValues(fieldName) };

    if (fieldName === 'startDate' || fieldName === 'dueDate') {
      data = { [fieldName]: formatDate3(getValues(fieldName)) };
    }

    if (tdmDefaultValues[fieldName] === getValues(fieldName)) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: `No changes were detected to update the ${tdmType}.`,
          toastType: 'info',
        })
      );
      editHandler(fieldName);
      return;
    }

    if (!Object.hasOwn(errors, fieldName)) {
      updateTdm.mutate(data);
      editHandler(fieldName);
    }
  };

  const cancelHandler = (fieldName: string) => {
    resetField(fieldName);
    editHandler(fieldName);
  };

  const editHandler = (fieldName: string) => {
    setIsEditing((prevState) => {
      return { ...prevState, [fieldName]: !prevState[fieldName] };
    });
    clearErrors(fieldName);
  };

  const fetchVendors = useSherpaVendors({});
  const vendors = fetchVendors?.data?.data;
  const startDate = watch('startDate');

  return (
    <>
      {showDeleteConfirm && (
        <Confirm
          title={`Delete ${tdmType}`}
          message={`You are about to delete ${tdm?.name} ${tdmType}. Are you sure you want to delete ?`}
          acceptButtonLabel='Delete'
          declineButtonLabel='Cancel'
          accept={() => removeTdm.mutate()}
          decline={cancelDeleteTdm}
        />
      )}
      {isEditing.tdmStatus && <ChangeStatusForm tdm={tdm} tdmType={tdmType} handleModal={statusModalHandler} />}
      <section id='tdm-description' aria-labelledby='applicant-information-title'>
        <div className='bg-white shadow sm:rounded-lg'>
          <div className='flex flex-col md:flex-row gap-4 md:justify-between lg:items-center px-4 py-5 sm:px-6'>
            <div id='tdm-details-name' className='flex-grow '>
              {!isEditing.name && (
                <div className='flex gap-4'>
                  <h2 id='applicant-information-title' className='m-0 mb-2 text-lg leading-6 font-medium text-neutralColor-dark'>
                    {tdm?.name}
                  </h2>
                  <PencilIcon
                    id='edit-name-button'
                    className='h-5 w-5 text-neutralColor-normal hover:text-green-400 cursor-pointer'
                    onClick={() => editHandler('name')}
                  />
                </div>
              )}
              {isEditing.name && (
                <div className='w-full mb-4 flex flex-col gap-2'>
                  <div className='w-full'>
                    <InputField name='name' type='text' error={errors?.name?.message} register={register} />
                  </div>
                  <div className='flex gap-2 items-center'>
                    <button
                      id='save-name-button'
                      type='button'
                      className='bg-green-500 text-white py-1 px-2 rounded-md cursor-pointer hover:bg-green-400'
                      onClick={() => updateTdmHandler('name')}
                    >
                      Save
                    </button>
                    <button
                      id='cancel-name-button'
                      type='button'
                      className='bg-gray-400 text-white py-1 px-2 rounded-md cursor-pointer hover:bg-gray-300'
                      onClick={() => cancelHandler('name')}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              <Badge status={tdm?.status} text={tdmStatus[tdm?.status]} />
            </div>
            <div id='tdm-details-change-status' className='flex flex-row gap-4 justify-between md:justify-center md:items-center'>
              <div
                id='change-status-button'
                className='w-full flex gap-2 px-2 items-center justify-center md:px-4 py-2 rounded-md bg-gray-200 cursor-pointer hover:bg-gray-300'
                onClick={statusModalHandler}
              >
                <ArrowsRightLeftIcon className='h-5 w-5' />
                <p>Change status</p>
              </div>
              <div
                id='delete-tdm-button'
                className='bg-neutralColor-lightest p-2 rounded-md cursor-pointer hover:bg-neutralColor-lighter  text-neutralColor-normal hover:text-tertiaryColor-normal'
                onClick={deleteTdmHandler}
              >
                <TrashIcon className='h-5 w-5 ' />
              </div>
            </div>
          </div>
          <div className='border-t border-gray-200 px-4 py-5 sm:px-6'>
            <dl className='grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2'>
              <div id='tdm-details-lead' className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500 flex gap-2 items-center'>Lead</dt>
                {tdm?.lead && !isEditing.leadId && (
                  <dd className='mt-1 text-sm text-gray-900 flex items-center gap-2'>
                    <ImageCircularAvatar imageUrl={tdm?.lead?.profileImageUrl} fallbackText={getInitials(tdm?.lead?.name)} />
                    <span>{tdm?.lead.name}</span>
                  </dd>
                )}
                {!tdm?.lead && !isEditing.leadId && <dd>None</dd>}
                {isEditing.leadId && (
                  <ReactSelect
                    name='leadId'
                    defaultValue={{
                      label: tdm?.lead?.name,
                      value: tdm?.lead?.id,
                    }}
                    options={leads}
                    onChange={(option: any) => {
                      const reactSelectOption = option as ReactSelectOption;
                      setValue('leadId', reactSelectOption?.value);
                    }}
                  />
                )}
                <dd className='text-sm underline mt-2 text-secondaryColor-normal cursor-pointer'>
                  {!isEditing.leadId && (
                    <button
                      id='edit-lead-button'
                      type='button'
                      className='hover:text-secondaryColor-light'
                      onClick={() => editHandler('leadId')}
                    >
                      Assign
                    </button>
                  )}
                  {isEditing.leadId && (
                    <div className='flex gap-2 '>
                      <button id='save-lead-button' className='hover:text-secondaryColor-light' onClick={() => updateTdmHandler('leadId')}>
                        Save
                      </button>
                      <button
                        id='cancel-lead-button'
                        className='text-gray-500 underline hover:text-neutralColor-light'
                        onClick={() => cancelHandler('leadId')}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </dd>
              </div>
              <div id='tdm-details-support' className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500 flex gap-2 items-center'>Support</dt>
                {tdm?.support && !isEditing.supportId && (
                  <dd className='mt-1 text-sm text-gray-900 flex items-center gap-2'>
                    <ImageCircularAvatar imageUrl={tdm?.support?.profileImageUrl} fallbackText={getInitials(tdm?.support?.name)} />
                    <span>{tdm?.support?.name}</span>
                  </dd>
                )}
                {!tdm?.support && !isEditing.supportId && <dd>None</dd>}
                {isEditing.supportId && (
                  <ReactSelect
                    name='supportId'
                    defaultValue={{
                      label: tdm?.support?.name,
                      value: tdm?.support?.id,
                    }}
                    options={supports}
                    onChange={(option) => {
                      const reactSelectOption = option as ReactSelectOption;
                      setValue('supportId', reactSelectOption?.value || null);
                    }}
                  />
                )}
                <dd className='text-sm underline mt-2 text-secondaryColor-normal cursor-pointer'>
                  {!isEditing.supportId && (
                    <button
                      id='edit-support-button'
                      type='button'
                      className='hover:text-secondaryColor-light'
                      onClick={() => editHandler('supportId')}
                    >
                      Assign
                    </button>
                  )}
                  {isEditing.supportId && (
                    <div className='flex gap-2 '>
                      <button
                        id='save-support-button'
                        type='button'
                        className='hover:text-secondaryColor-light'
                        onClick={() => updateTdmHandler('supportId')}
                      >
                        Save
                      </button>
                      <button
                        id='cancel-support-button'
                        type='button'
                        className='text-gray-500 underline hover:text-neutralColor-light'
                        onClick={() => cancelHandler('supportId')}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </dd>
              </div>
              <div className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>Phase</dt>
                <dd className='mt-1 text-sm text-gray-900 capitalize'>{phase?.label}</dd>
              </div>

              <div id='tdm-details-vendor' className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>Vendor</dt>
                {tdm?.vendor && !isEditing.vendorId && <dd className='mt-1 text-sm text-gray-900'>{tdm?.vendor?.name}</dd>}
                {!tdm?.vendor && !isEditing?.vendor && <dd>None</dd>}
                {isEditing.vendorId && (
                  <ReactSelect<GetVendorsResponse>
                    name='vendorId'
                    defaultValue={tdm?.vendor}
                    options={vendors}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => String(option.id)}
                    onChange={(option) => {
                      setValue('vendorId', option?.id || null);
                    }}
                  />
                )}
                <dd className='text-sm underline mt-2 text-secondaryColor-normal cursor-pointer'>
                  {!isEditing.vendorId && (
                    <button
                      id='edit-vendor-button'
                      type='button'
                      className='hover:text-secondaryColor-light'
                      onClick={() => editHandler('vendorId')}
                    >
                      Assign
                    </button>
                  )}
                  {isEditing.vendorId && (
                    <div className='flex gap-2 '>
                      <button
                        id='save-vendor-button'
                        type='button'
                        className='hover:text-secondaryColor-light'
                        onClick={() => updateTdmHandler('vendorId')}
                      >
                        Save
                      </button>
                      <button
                        id='cancel-vendor-button'
                        type='button'
                        className='text-gray-500 underline hover:text-neutralColor-light'
                        onClick={() => cancelHandler('vendorId')}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </dd>
              </div>

              <div id='tdm-details-start-date' className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>Start Date</dt>
                <dd className='mt-1 text-sm text-gray-900'>
                  {tdm?.startDate && !isEditing.startDate && (
                    <span className='mt-1 text-sm text-gray-900'>{formatDate3(tdm?.startDate)}</span>
                  )}
                  {!tdm?.startDate && !isEditing.startDate && <span>None</span>}
                  {isEditing.startDate && (
                    <InputField name='startDate' type='date' error={errors?.startDate?.message} register={register} />
                  )}
                </dd>
                <dd className='text-sm underline mt-2 text-secondaryColor-normal cursor-pointer'>
                  {!isEditing.startDate && (
                    <button
                      id='edit-due-date-button'
                      type='button'
                      className='hover:text-secondaryColor-light'
                      onClick={() => editHandler('startDate')}
                    >
                      Assign
                    </button>
                  )}
                  {isEditing.startDate && (
                    <div className='flex gap-2 '>
                      <button
                        id='save-due-date-button'
                        type='button'
                        className='hover:text-secondaryColor-light'
                        onClick={() => updateTdmHandler('startDate')}
                      >
                        Save
                      </button>
                      <button
                        id='cancel-due-date-button'
                        type='button'
                        className='text-gray-500 underline hover:text-neutralColor-light'
                        onClick={() => cancelHandler('startDate')}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </dd>
              </div>
              <div id='tdm-details-due-date' className='sm:col-span-1'>
                <dt className='text-sm font-medium text-gray-500'>Due Date</dt>
                <dd className='mt-1 text-sm text-gray-900'>
                  {tdm?.dueDate && !isEditing.dueDate && <span className='mt-1 text-sm text-gray-900'>{formatDate3(tdm?.dueDate)}</span>}
                  {!tdm?.dueDate && !isEditing.dueDate && <span>None</span>}
                  {isEditing.dueDate && (
                    <InputField
                      name='dueDate'
                      type='date'
                      error={errors?.dueDate?.message}
                      min={startDate || undefined}
                      register={register}
                    />
                  )}
                </dd>
                <dd className='text-sm underline mt-2 text-secondaryColor-normal cursor-pointer'>
                  {!isEditing.dueDate && (
                    <button
                      id='edit-due-date-button'
                      type='button'
                      className='hover:text-secondaryColor-light'
                      onClick={() => editHandler('dueDate')}
                    >
                      Assign
                    </button>
                  )}
                  {isEditing.dueDate && (
                    <div className='flex gap-2 '>
                      <button
                        id='save-due-date-button'
                        type='button'
                        className='hover:text-secondaryColor-light'
                        onClick={() => updateTdmHandler('dueDate')}
                      >
                        Save
                      </button>
                      <button
                        id='cancel-due-date-button'
                        type='button'
                        className='text-gray-500 underline hover:text-neutralColor-light'
                        onClick={() => cancelHandler('dueDate')}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </dd>
              </div>

              <div id='tdm-details-notes' className='sm:col-span-2'>
                <dt className='text-sm font-medium text-gray-500'>Notes</dt>
                {!isEditing.notes && <dd className='mt-1 text-sm text-gray-900 break-words'>{tdm?.notes ? tdm.notes : 'None'}</dd>}
                {isEditing.notes && (
                  <dd className='flex flex-col'>
                    <textarea
                      className='border rounded-md py-2 px-3 focus:outline-hidden focus:ring focus:ring-primaryColor-light'
                      rows={4}
                      cols={50}
                      maxLength={1001}
                      {...register('notes')}
                    />
                    <span className='mt-2 text-xs'>1000 max characters</span>
                    <span className='text-tertiaryColor-normal mt-2'>{errors?.notes?.message?.toString()}</span>
                  </dd>
                )}
                <dd className='text-sm underline mt-2 text-secondaryColor-normal cursor-pointer'>
                  {!isEditing.notes && (
                    <button
                      id='edit-notes-button'
                      type='button'
                      className='hover:text-secondaryColor-light'
                      onClick={() => editHandler('notes')}
                    >
                      Edit
                    </button>
                  )}
                  {isEditing.notes && (
                    <div className='flex gap-2 '>
                      <button
                        id='save-notes-button'
                        type='button'
                        className='hover:text-secondaryColor-light'
                        onClick={() => updateTdmHandler('notes')}
                      >
                        Save
                      </button>
                      <button
                        id='cancel-notes-button'
                        type='button'
                        className='text-gray-500 underline hover:text-neutralColor-light'
                        onClick={() => cancelHandler('notes')}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </section>
    </>
  );
}
